import { useEffect, useLayoutEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ApiOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  BellOutlined,
  BulbOutlined,
  BookOutlined,
  BugOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  CodeOutlined,
  CompassOutlined,
  CopyOutlined,
  CommentOutlined,
  CrownOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  FormatPainterOutlined,
  FormOutlined,
  GiftOutlined,
  HeartOutlined,
  HighlightOutlined,
  HomeOutlined,
  PictureOutlined,
  LinkOutlined,
  LockOutlined,
  LoginOutlined,
  LogoutOutlined,
  MenuOutlined,
  MessageOutlined,
  MoneyCollectOutlined,
  PieChartOutlined,
  QuestionCircleOutlined,
  RadarChartOutlined,
  ReadOutlined,
  ReloadOutlined,
  RobotOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SearchOutlined,
  SendOutlined,
  TagsOutlined,
  ShoppingCartOutlined,
  SmileOutlined,
  SwapOutlined,
  SyncOutlined,
  TeamOutlined,
  ToolOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WalletOutlined
} from '@ant-design/icons';
import {
  Avatar,
  Badge,
  Button,
  Carousel,
  Dropdown,
  Drawer,
  Empty,
  Input,
  Layout,
  Menu,
  Progress,
  Result,
  Select,
  Space,
  Spin,
  Typography
} from 'antd';
import type { MenuProps } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { AccountRestriction, HeaderUser, NavItem } from '../types/frontV2';
import { FRONT_CURRENT_USER_CACHE_KEY, clearFrontSessionCaches, readCachedHeaderUser } from '../utils/cache';
import { ensureFrontV2ImSocket, onFrontV2ImEvent, refreshFrontV2ImUnreadCount } from '../utils/imSocket';
import { SiteFooter } from './SiteFooter';

const { Header, Content } = Layout;

export const toolItems: NavItem[] = [
  { key: '/ranklist', icon: <BarChartOutlined />, label: '排名' },
  { key: '/report', icon: <PieChartOutlined />, label: '报表' },
  { key: '/schedules', icon: <BookOutlined />, label: '题单' },
  { key: '/api-doc', icon: <ApiOutlined />, label: '开发' },
  { key: '/oauth/apps', icon: <AppstoreOutlined />, label: '应用' },
  { key: '/statement', icon: <FileTextOutlined />, label: '组题' },
  { key: '/image-hosting', icon: <PictureOutlined />, label: '图床' },
  { key: '/graph-editor', icon: <LinkOutlined />, label: '画图' },
  { key: '/peoples', icon: <TeamOutlined />, label: '人物' },
  { key: '/help', icon: <QuestionCircleOutlined />, label: '帮助' }
];

function getVisibleToolItems(currentUser: HeaderUser | null): NavItem[] {
  return toolItems.filter((item) => item.key !== '/oauth/apps' || !!currentUser);
}

function getNavItems(currentUser: HeaderUser | null): NavItem[] {
  return [
  { key: '/home', icon: <HomeOutlined />, label: '首页' },
  { key: '/problems', icon: <CodeOutlined />, label: '题库' },
  { key: '/contests', icon: <CrownOutlined />, label: '比赛' },
  { key: '/submissions', icon: <RadarChartOutlined />, label: '评测' },
  { key: '/discussions', icon: <CommentOutlined />, label: '讨论' },
  { key: '/courses', icon: <BookOutlined />, label: '课程' },
  { key: '/square', icon: <HighlightOutlined />, label: '笺书' },
  ];
}

const userMenuItems: MenuProps['items'] = [
  { key: 'profile', icon: <UserOutlined />, label: '我的主页' },
  { key: 'edit', icon: <SafetyCertificateOutlined />, label: '修改资料' },
  { key: 'myProblems', icon: <DatabaseOutlined />, label: '我的题目' },
  { key: 'promote', icon: <GiftOutlined />, label: '邀请有币' },
  { key: 'unlock', icon: <LockOutlined />, label: '锁定账户' },
  { key: 'tools', icon: <ToolOutlined />, label: '工具' },
  { type: 'divider' },
  { key: 'shop', icon: <ShoppingCartOutlined />, label: '金币商店' },
  { key: 'files', icon: <CloudOutlined />, label: '我的网盘' },
  { key: 'videoBucket', icon: <VideoCameraOutlined />, label: '视频仓库' },
  { key: 'switchUser', icon: <SyncOutlined />, label: '切换用户' },
  { type: 'divider' },
  { key: 'admin', icon: <DashboardOutlined />, label: '后台管理' },
  { key: 'login', icon: <LoginOutlined />, label: '登录' },
  { key: 'logout', icon: <LogoutOutlined />, label: '注销' }
];

const menuRouteMap: Record<string, string> = {
  myProblems: '/front-v2/problems?mine=1',
  promote: '/front-v2/account/promote',
  unlock: '/front-v2/account/lock',
  shop: '/front-v2/shop',
  files: '/front-v2/files',
  videoBucket: '/front-v2/video-bucket',
  switchUser: '/front-v2/switch-user',
  admin: '/admin-v2',
  login: '/front-v2/login?url=/front-v2/home',
  logout: '/logout?url=/front-v2'
};

const FRONT_SURFACE_THEME_KEY = 'front-v2-surface-theme';
const FRONT_DARK_MODE_KEY = 'front-v2-dark-mode';
const surfaceThemeOptions = [
  { key: 'mist', label: '柔和灰', swatch: '#f3f5f7' },
  { key: 'blue', label: '雾蓝', swatch: '#f1f6fb' },
  { key: 'sage', label: '鼠尾草', swatch: '#f2f7f3' },
  { key: 'orange', label: '温馨橙', swatch: '#fcf6ef' },
  { key: 'purple', label: '淡紫', swatch: '#f6f2fb' },
  { key: 'warm', label: '暖灰', swatch: '#faf7f1' },
  { key: 'rose', label: '柔粉', swatch: '#fbf3f6' },
  { key: 'cyan', label: '海盐青', swatch: '#eef8f7' },
  { key: 'yellow', label: '晨光黄', swatch: '#faf8ed' }
] as const;
type SurfaceThemeKey = typeof surfaceThemeOptions[number]['key'];

function isSurfaceThemeKey(value: string): value is SurfaceThemeKey {
  return surfaceThemeOptions.some((option) => option.key === value);
}

function readSurfaceTheme(): SurfaceThemeKey {
  if (typeof window === 'undefined') return 'orange';
  try {
    const value = window.localStorage.getItem(FRONT_SURFACE_THEME_KEY);
    return value && isSurfaceThemeKey(value) ? value : 'orange';
  } catch (e) {
    return 'orange';
  }
}

function readDarkMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FRONT_DARK_MODE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function toMenuItems(items: NavItem[]): MenuProps['items'] {
  return items.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: item.children ? item.label : <Link to={item.key}>{item.label}</Link>,
    popupClassName: item.popupClassName,
    popupOffset: item.popupOffset,
    children: item.children?.map((child) => ({
      key: child.key,
      icon: child.icon,
      label: <Link to={child.key}>{child.label}</Link>
    }))
  }));
}

function getHeaderPopupContainer(triggerNode: HTMLElement): HTMLElement {
  return triggerNode.closest<HTMLElement>('.front-v2-header') || document.body;
}

function getVisibleUserMenuItems(currentUser: HeaderUser | null): MenuProps['items'] {
  return (userMenuItems || []).filter((item: any) => {
    if (!item || item.type === 'divider') return !!currentUser;
    if (item.key === 'login') return !currentUser;
    if (item.key === 'logout') return !!currentUser;
    if (item.key === 'admin') return !!currentUser?.isAdmin;
    if (item.key === 'videoBucket') return Number(currentUser?.id) === 1;
    if (item.key === 'switchUser') return Number(currentUser?.id) === 1;
    return !!currentUser;
  }).map((item: any) => item?.key === 'tools' ? {
    ...item,
    popupClassName: 'front-v2-user-tools-menu',
    children: getVisibleToolItems(currentUser).map((tool) => ({
      key: tool.key,
      icon: tool.icon,
      className: 'front-v2-user-tool-item',
      label: <Link to={tool.key}>{tool.label}</Link>
    }))
  } : item);
}

function runUserMenuRoute(key: string, navigate: ReturnType<typeof useNavigate>, currentUser: HeaderUser | null) {
  if (key === 'logout') clearFrontSessionCaches();
  if (key === 'profile' && currentUser?.id) {
    navigate(`/users/${currentUser.id}`);
    return;
  }
  if (key === 'edit' && currentUser?.id) {
    navigate('/account/profile-edit');
    return;
  }
  const route = menuRouteMap[key];
  if (!route) return;
  const frontRoute = frontV2RouteFromUrl(route);
  if (frontRoute) navigate(frontRoute);
  else window.location.href = route;
}

function frontV2RouteFromUrl(url: string) {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin || !parsed.pathname.startsWith('/front-v2')) return '';
    const routePath = parsed.pathname.slice('/front-v2'.length) || '/';
    return `${routePath}${parsed.search}${parsed.hash}`;
  } catch (e) {
    return '';
  }
}

function scrollKey(locationKey: string) {
  return `front-v2-scroll:${locationKey}`;
}

function saveScrollPosition(locationKey: string) {
  try {
    window.sessionStorage.setItem(scrollKey(locationKey), JSON.stringify({ x: window.scrollX, y: window.scrollY }));
  } catch (e) {
    // Scroll restoration is best-effort.
  }
}

function restoreScrollPosition(locationKey: string, hash: string, restoreSavedPosition = true) {
  let cancelled = false;
  const timers: number[] = [];
  let savedPosition: { x?: number; y?: number } | null = null;
  if (!hash && restoreSavedPosition) {
    try {
      const raw = window.sessionStorage.getItem(scrollKey(locationKey));
      savedPosition = raw ? JSON.parse(raw) : null;
    } catch (e) {
      savedPosition = null;
    }
  }
  const restore = () => {
    if (cancelled) return;
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      const escapedId = window.CSS?.escape ? window.CSS.escape(id) : id.replace(/["\\]/g, '\\$&');
      const target = document.getElementById(id) || document.querySelector(`[name="${escapedId}"]`);
      if (target instanceof HTMLElement) target.scrollIntoView();
      return;
    }
    window.scrollTo(savedPosition?.x || 0, savedPosition?.y || 0);
  };
  const delays = hash || savedPosition ? [0, 80, 240, 600] : [0];
  delays.forEach((delay) => {
    const timer = window.setTimeout(() => window.requestAnimationFrame(restore), delay);
    timers.push(timer);
  });
  return () => {
    cancelled = true;
    timers.forEach((timer) => window.clearTimeout(timer));
  };
}

function frontV2ContentLooksBlank() {
  const root = document.getElementById('root');
  if (!root) return true;
  const shell = root.querySelector('.front-v2-shell');
  if (!shell) return true;
  const content = root.querySelector<HTMLElement>('.front-v2-content, .front-v2-auth-content');
  if (!content) return true;
  const meaningfulNode = content.querySelector('.front-v2-page, .loading-line, .ant-spin, .ant-empty, main, section, article, form, table, canvas, svg, img, video, iframe');
  const textLength = (content.textContent || '').replace(/\s+/g, '').length;
  return !meaningfulNode && textLength === 0;
}

export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const authRouteSet = new Set(['/login', '/register', '/ticket', '/forgot-password', '/forgot-password/reset', '/terms', '/privacy', '/youth']);
  const isAuthRoute = authRouteSet.has(location.pathname);
  const isPublicContentRoute = location.pathname.startsWith('/ai/share/');
  const [currentUser, setCurrentUser] = useState<HeaderUser | null>(() => readCachedHeaderUser());
  const [accountRestriction, setAccountRestriction] = useState<AccountRestriction | null>(null);
  const [headerAuthReady, setHeaderAuthReady] = useState(false);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [restoreVersion, setRestoreVersion] = useState(0);
  const [isCompactShell, setIsCompactShell] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [surfaceTheme, setSurfaceTheme] = useState<SurfaceThemeKey>(() => readSurfaceTheme());
  const [darkMode, setDarkMode] = useState<boolean>(() => readDarkMode());
  const [preliminaryVisible, setPreliminaryVisible] = useState(false);
  const visibleNavItems = getNavItems(currentUser);
  const matched = visibleNavItems.find((item) => !item.children && (location.pathname === item.key || location.pathname.startsWith(`${item.key}/`)));
  const selectedKey = matched?.key || '';
  const currentFullPath = `/front-v2${location.pathname}${location.search}${location.hash}`;
  const authReturnQuery = `?url=${encodeURIComponent(currentFullPath)}`;
  const contentRestoreKey = `${location.key}:${restoreVersion}`;
  const mobileUserMenuItems = getVisibleUserMenuItems(currentUser);
  const submitHeaderSearch = () => {
    const keyword = searchKeyword.trim();
    if (!keyword) return;
    navigate(`/search?keyword=${encodeURIComponent(keyword)}`);
    setMobileSearchOpen(false);
    setMobileMenuOpen(false);
  };

  const onUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    runUserMenuRoute(String(key), navigate, currentUser);
    setMobileMenuOpen(false);
  };

  const onSurfaceThemeMenuClick: MenuProps['onClick'] = ({ key }) => {
    const nextTheme = String(key);
    if (isSurfaceThemeKey(nextTheme)) setSurfaceTheme(nextTheme);
  };

  const surfaceThemeMenuItems: MenuProps['items'] = [
    ...surfaceThemeOptions.map((option) => ({
      key: option.key,
      label: (
        <span className="front-v2-surface-theme-option">
          <i className="front-v2-surface-theme-swatch" data-surface-theme={option.key} />
          <span>{option.label}</span>
          {surfaceTheme === option.key ? <CheckOutlined /> : null}
        </span>
      )
    }))
  ];

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    let alive = true;
    if (!currentUser) {
      setPreliminaryVisible(false);
      return () => void (alive = false);
    }
    if (currentUser.isAdmin) {
      setPreliminaryVisible(true);
      return () => void (alive = false);
    }
    fetch('/api/front-v2/preliminary-exams/navigation', {
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.json())
      .then((payload) => {
        if (alive) setPreliminaryVisible(!!payload?.success && !!payload.visible);
      })
      .catch(() => {
        if (alive) setPreliminaryVisible(false);
      });
    return () => void (alive = false);
  }, [currentUser?.id, currentUser?.isAdmin]);

  useLayoutEffect(() => {
    document.documentElement.dataset.frontSurfaceTheme = surfaceTheme;
    try {
      window.localStorage.setItem(FRONT_SURFACE_THEME_KEY, surfaceTheme);
    } catch (e) {
      // Theme persistence is best-effort; the active page still updates immediately.
    }
  }, [surfaceTheme]);

  useLayoutEffect(() => {
    document.documentElement.dataset.frontDark = darkMode ? 'true' : 'false';
    try {
      window.localStorage.setItem(FRONT_DARK_MODE_KEY, darkMode ? '1' : '0');
    } catch (e) {
      // Theme persistence is best-effort; the active page still updates immediately.
    }
  }, [darkMode]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === FRONT_DARK_MODE_KEY) setDarkMode(event.newValue === '1');
    };
    const onDarkModeChange = (event: Event) => {
      const enabled = (event as CustomEvent<{ enabled?: boolean }>).detail?.enabled;
      if (typeof enabled === 'boolean') setDarkMode(enabled);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('front-v2:dark-mode', onDarkModeChange);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('front-v2:dark-mode', onDarkModeChange);
    };
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== FRONT_SURFACE_THEME_KEY || !event.newValue || !isSurfaceThemeKey(event.newValue)) return;
      setSurfaceTheme(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const compactQuery = window.matchMedia('(max-width: 767.98px)');
    const updateCompactShell = () => setIsCompactShell(compactQuery.matches);
    updateCompactShell();
    compactQuery.addEventListener('change', updateCompactShell);
    return () => compactQuery.removeEventListener('change', updateCompactShell);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname, location.search]);

  useLayoutEffect(() => {
    const isFreshDocumentEntry = location.key === 'default';
    const cancelRestore = restoreScrollPosition(location.key, location.hash, !isFreshDocumentEntry);
    return () => {
      cancelRestore();
      if (!isFreshDocumentEntry) saveScrollPosition(location.key);
    };
  }, [location.key, location.hash]);

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>('a[href]');
      if (!anchor || anchor.target && anchor.target !== '_self' || anchor.hasAttribute('download')) return;
      const route = frontV2RouteFromUrl(anchor.href);
      if (!route) return;
      event.preventDefault();
      navigate(route);
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [navigate]);

  useEffect(() => {
    const recoverBlankContent = () => {
      window.setTimeout(() => {
        if (!frontV2ContentLooksBlank()) return;
        setRestoreVersion((value) => value + 1);
        window.setTimeout(() => {
          if (frontV2ContentLooksBlank()) window.location.reload();
        }, 360);
      }, 120);
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      setRestoreVersion((value) => value + 1);
      window.dispatchEvent(new Event('front-v2:pageshow-restore'));
      window.dispatchEvent(new Event('front-v2:refresh-unread'));
      recoverBlankContent();
    };
    const onPopState = () => recoverBlankContent();
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  useEffect(() => {
    if (isAuthRoute) return undefined;
    let alive = true;
    const keepCurrentSession = () => {
      if (alive) setHeaderAuthReady(true);
    };
    fetch('/api/front-v2/auth/bootstrap', {
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
      .then(async (response) => {
        const contentType = response.headers.get('content-type') || '';
        const payload = contentType.includes('application/json') ? await response.json().catch(() => null) : null;
        return { response, payload };
      })
      .then((payload) => {
        if (!alive) return;
        const { response, payload: data } = payload;
        const restriction = data?.accountRestriction;
        if (restriction?.type === 'disabled' || restriction?.type === 'expired') {
          setCurrentUser(null);
          setAccountRestriction({
            type: restriction.type,
            title: String(restriction.title || (restriction.type === 'disabled' ? '账号已被禁用' : '账号已过期')),
            message: String(restriction.message || '当前账号暂时无法使用，请联系管理员处理。')
          });
          setHeaderAuthReady(true);
          clearFrontSessionCaches();
          return;
        }
        setAccountRestriction(null);
        const unauthenticated = response.status === 401 || data?.needLogin === true || data?.authenticated === false;
        if (unauthenticated) {
          setCurrentUser(null);
          setHeaderAuthReady(true);
          clearFrontSessionCaches();
          return;
        }
        if (!response.ok || !data?.success || !data.user) {
          keepCurrentSession();
          return;
        }
        setCurrentUser(data.user);
        if (typeof data.user?.darkMode === 'boolean') setDarkMode(data.user.darkMode);
        setHeaderAuthReady(true);
        if (data.user?.needsTicket && location.pathname !== '/ticket') {
          window.location.href = `/front-v2/ticket?url=${encodeURIComponent(currentFullPath)}`;
          return;
        }
        try {
          window.sessionStorage.setItem(FRONT_CURRENT_USER_CACHE_KEY, JSON.stringify(data.user));
        } catch (e) {
          // Session cache is best-effort; the API remains the source of truth.
        }
      })
      .catch(() => {
        keepCurrentSession();
      });
    return () => {
      alive = false;
    };
  }, [isAuthRoute, location.pathname, currentFullPath, restoreVersion]);

  useEffect(() => {
    if (isAuthRoute || isPublicContentRoute || !headerAuthReady || currentUser || accountRestriction) return;
    navigate(`/login${authReturnQuery}`, { replace: true });
  }, [accountRestriction, authReturnQuery, currentUser, headerAuthReady, isAuthRoute, isPublicContentRoute, navigate]);

  useEffect(() => {
    if (isAuthRoute || !currentUser || !headerAuthReady) {
      setMessageUnreadCount(0);
      return undefined;
    }
    let alive = true;
    const syncUnreadCount = () => {
      refreshFrontV2ImUnreadCount()
        .then((payload) => {
          if (alive) setMessageUnreadCount(Number(payload.count || 0));
        })
        .catch(() => {
          if (alive) setMessageUnreadCount(0);
        });
    };
    const applyUnreadPayload = (payload: Record<string, unknown>) => {
      if (!alive) return;
      if (payload.system_notice_changed) {
        syncUnreadCount();
        return;
      }
      if (payload.count != null) {
        setMessageUnreadCount(Math.max(0, Number(payload.count || 0)));
      }
    };
    const removeUnreadListener = onFrontV2ImEvent('unread-count-update', applyUnreadPayload);
    const removeNewMessageListener = onFrontV2ImEvent('new-message', (payload) => {
      if (!alive) return;
      if (payload.unread_count != null) {
        setMessageUnreadCount(Math.max(0, Number(payload.unread_count || 0)));
        return;
      }
      if (payload.unread_delta != null) {
        setMessageUnreadCount((value) => Math.max(0, value + Number(payload.unread_delta || 0)));
      }
    });
    const removeReadListener = onFrontV2ImEvent('conversation-read', syncUnreadCount);
    const removeReadyListener = onFrontV2ImEvent('socket-ready', syncUnreadCount);
    syncUnreadCount();
    ensureFrontV2ImSocket().catch(() => undefined);
    const onVisible = () => {
      if (document.hidden) return;
      ensureFrontV2ImSocket().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('front-v2:refresh-unread', syncUnreadCount);
    return () => {
      alive = false;
      removeUnreadListener();
      removeNewMessageListener();
      removeReadListener();
      removeReadyListener();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('front-v2:refresh-unread', syncUnreadCount);
    };
  }, [isAuthRoute, currentUser?.id, headerAuthReady]);

  if (isAuthRoute) {
    return (
      <Layout className="front-v2-shell auth-shell-v2">
        <Content className="front-v2-auth-content" key={contentRestoreKey}>{children}</Content>
      </Layout>
    );
  }

  if (headerAuthReady && accountRestriction) {
    return (
      <Layout className={isCompactShell ? 'front-v2-shell front-v2-shell-compact' : 'front-v2-shell'}>
        <Content className="front-v2-content" key={contentRestoreKey}>
          <div className="front-v2-page front-v2-account-restriction">
            <Result
              status={accountRestriction.type === 'disabled' ? 'error' : 'warning'}
              title={accountRestriction.title}
              subTitle={accountRestriction.message}
              extra={<Button type="primary" href="/logout?url=/front-v2/login">退出当前账号</Button>}
            />
          </div>
        </Content>
      </Layout>
    );
  }

  if ((!headerAuthReady || !currentUser) && !isPublicContentRoute) {
    return (
      <Layout className={isCompactShell ? 'front-v2-shell front-v2-shell-compact' : 'front-v2-shell'}>
        <Content className="front-v2-content" key={contentRestoreKey}>
          <div className="front-v2-page">
            <div className="front-v2-placeholder">
              <Spin size="large" />
            </div>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className={isCompactShell ? 'front-v2-shell front-v2-shell-compact' : 'front-v2-shell'}>
      <Header className="front-v2-header">
        <div className="front-v2-header-inner">
          <a href="/" className="front-v2-brand" aria-label="返回新版着陆页">
            <span className="front-v2-brand-mark">B</span>
            <span className="front-v2-brand-name">Becoder</span>
          </a>
          <Menu
            mode="horizontal"
            selectedKeys={selectedKey ? [selectedKey] : []}
            items={toMenuItems(visibleNavItems)}
            className="front-v2-nav"
            getPopupContainer={getHeaderPopupContainer}
          />
          <Space size={10} className="front-v2-actions">
            {!isCompactShell ? (
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="ID / 题目名 / 用户 / 手机号 / 比赛"
                className="front-v2-search"
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
                onPressEnter={submitHeaderSearch}
              />
            ) : (
              <Button
                type="text"
                className="front-v2-mobile-menu front-v2-search-trigger"
                icon={<SearchOutlined />}
                aria-label="搜索"
                onClick={() => setMobileSearchOpen(true)}
              />
            )}
            <Dropdown
              menu={{ items: surfaceThemeMenuItems, selectable: true, selectedKeys: [surfaceTheme], onClick: onSurfaceThemeMenuClick }}
              overlayClassName="front-v2-surface-menu"
              placement="bottomRight"
              trigger={['click']}
              getPopupContainer={getHeaderPopupContainer}
            >
              <Button
                type="text"
                className="front-v2-theme-trigger"
                icon={<FormatPainterOutlined />}
                aria-label="界面背景"
                title="界面背景"
              />
            </Dropdown>
            <Badge count={messageUnreadCount} size="small" overflowCount={99} className="front-v2-message-badge">
              <Button
                icon={<BellOutlined />}
                aria-label="通知"
                className={messageUnreadCount > 0 ? 'front-v2-message-button has-unread' : 'front-v2-message-button'}
                href={headerAuthReady && currentUser ? '/front-v2/messages' : `/front-v2/login${authReturnQuery}`}
              />
            </Badge>
            {isCompactShell ? (
              <>
                {currentUser ? (
                  <Dropdown
                    menu={{ items: mobileUserMenuItems, onClick: onUserMenuClick }}
                    overlayClassName="front-v2-user-menu"
                    placement="bottomRight"
                    trigger={['click']}
                    getPopupContainer={getHeaderPopupContainer}
                  >
                    <Button type="text" className="front-v2-mobile-account" aria-label="账号">
                      <Avatar size={28} src={currentUser.avatar} icon={!currentUser.avatar ? <UserOutlined /> : undefined} />
                    </Button>
                  </Dropdown>
                ) : (
                  <Button type="primary" size="small" href={`/front-v2/login${authReturnQuery}`} className="front-v2-mobile-login">
                    登录
                  </Button>
                )}
                <Button
                  className="front-v2-mobile-menu front-v2-mobile-nav-trigger"
                  icon={<MenuOutlined />}
                  aria-label="导航菜单"
                  onClick={() => setMobileMenuOpen(true)}
                />
              </>
            ) : (
              <span className={currentUser ? 'front-v2-auth-slot' : 'front-v2-auth-slot is-guest'}>
                {!headerAuthReady && currentUser ? (
                  <Button type="text" className="front-v2-user-button is-loading" aria-hidden="true" tabIndex={-1}>
                    <Avatar size={28} src={currentUser.avatar} icon={!currentUser.avatar ? <UserOutlined /> : undefined} />
                    <span>{currentUser.username || currentUser.nickname || '用户'}</span>
                  </Button>
                ) : currentUser ? (
                  <Dropdown
                    menu={{ items: getVisibleUserMenuItems(currentUser), onClick: onUserMenuClick }}
                    overlayClassName="front-v2-user-menu"
                    placement="bottomRight"
                    trigger={['click']}
                    getPopupContainer={getHeaderPopupContainer}
                  >
                    <Button type="text" className="front-v2-user-button">
                      <Avatar size={28} src={currentUser.avatar} icon={!currentUser.avatar ? <UserOutlined /> : undefined} />
                      <span>{currentUser.username || currentUser.nickname || '用户'}</span>
                    </Button>
                  </Dropdown>
                ) : (
                  <div className="front-v2-auth-actions">
                    <Button type="primary" href={`/front-v2/login${authReturnQuery}`}>登录</Button>
                  </div>
                )}
              </span>
            )}
          </Space>
        </div>
      </Header>
      <Content className={`front-v2-content ${location.pathname === '/ai/assistant' || location.pathname === '/ai/chat' || location.pathname.startsWith('/ai/problems/') ? 'ai-workspace-content-v3' : ''}`} key={contentRestoreKey}>{children}</Content>
      <SiteFooter />
      <Drawer
        title="搜索与导航"
        placement="right"
        width={360}
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        className="front-v2-mobile-drawer"
        destroyOnClose
      >
        <div className="front-v2-mobile-drawer-stack">
          <Input.Search
            allowClear
            value={searchKeyword}
            placeholder="ID / 题目名 / 用户 / 手机号 / 比赛"
            enterButton="搜索"
            onChange={(event) => setSearchKeyword(event.target.value)}
            onSearch={submitHeaderSearch}
          />
          <section className="front-v2-mobile-drawer-section">
            <Typography.Text className="front-v2-mobile-drawer-label">导航</Typography.Text>
            <Menu
              mode="inline"
              selectedKeys={selectedKey ? [selectedKey] : []}
              items={toMenuItems(visibleNavItems)}
              className="front-v2-mobile-drawer-menu"
              onClick={() => setMobileMenuOpen(false)}
            />
          </section>
          <section className="front-v2-mobile-drawer-section front-v2-surface-theme-section">
            <Typography.Text className="front-v2-mobile-drawer-label">界面背景</Typography.Text>
            <div className="front-v2-surface-theme-grid">
              {surfaceThemeOptions.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  className={surfaceTheme === option.key ? 'is-selected' : undefined}
                  aria-pressed={surfaceTheme === option.key}
                  onClick={() => setSurfaceTheme(option.key)}
                >
                  <i className="front-v2-surface-theme-swatch" data-surface-theme={option.key} />
                  <span>{option.label}</span>
                  {surfaceTheme === option.key ? <CheckOutlined /> : null}
                </button>
              ))}
              <button
                type="button"
                className={darkMode ? 'is-selected front-v2-dark-mode-button' : 'front-v2-dark-mode-button'}
                aria-pressed={darkMode}
                onClick={() => setDarkMode((current) => !current)}
              >
                <BulbOutlined />
                <span>暗黑模式</span>
                {darkMode ? <CheckOutlined /> : null}
              </button>
            </div>
          </section>
          {currentUser ? (
            <section className="front-v2-mobile-drawer-section">
              <Typography.Text className="front-v2-mobile-drawer-label">账号</Typography.Text>
            <Menu
              mode="inline"
              items={mobileUserMenuItems}
              className="front-v2-mobile-drawer-menu"
              onClick={({ key }) => {
                runUserMenuRoute(String(key), navigate, currentUser);
                setMobileMenuOpen(false);
              }}
            />
          </section>
          ) : null}
        </div>
      </Drawer>
      <Drawer
        title="搜索"
        placement="top"
        height={180}
        open={mobileSearchOpen}
        onClose={() => setMobileSearchOpen(false)}
        className="front-v2-search-drawer"
        destroyOnClose
      >
        <Input.Search
          allowClear
          value={searchKeyword}
          placeholder="ID / 题目 / 用户 / 手机 / 比赛"
          enterButton="搜索"
          onChange={(event) => setSearchKeyword(event.target.value)}
          onSearch={submitHeaderSearch}
        />
      </Drawer>
    </Layout>
  );
}
